import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { AngularComponentFactory, QuestionAngular } from 'survey-angular-ui';
import { Serializer, Question } from 'survey-core';
import * as L from 'leaflet';

export class QuestionMapPolygonModel extends Question {
  public override getType(): string {
    return 'map-polygon';
  }
}

export function registerMapPolygonQuestion(): void {
  if (!Serializer.findClass('map-polygon')) {
    Serializer.addClass(
      'map-polygon',
      [],
      function () {
        return new QuestionMapPolygonModel('');
      },
      'question'
    );
  }
  AngularComponentFactory.Instance.registerComponent(
    'map-polygon-question',
    MapPolygonQuestionComponent
  );
}

@Component({
  selector: 'app-map-polygon-question',
  templateUrl: './map-polygon-question.component.html',
  styleUrls: ['./map-polygon-question.component.scss'],
})
export class MapPolygonQuestionComponent
  extends QuestionAngular<QuestionMapPolygonModel>
  implements AfterViewInit, OnDestroy
{
  @ViewChild('map', { static: false }) mapElement!: ElementRef;
  private map!: L.Map;
  public polygonPath: L.LatLng[] = [];
  private currentPolygon: L.Polygon | null = null;
  private tempPolygon: L.Polygon | null = null;
  private vertexMarkers: L.Marker[] = [];
  public isDrawingMode: boolean = true;
  public isPolygonClosed: boolean = false;

  public get primaryColor(): string {
    if (this.mapElement?.nativeElement) {
      const color = getComputedStyle(this.mapElement.nativeElement)
        .getPropertyValue('--primary-color')
        ?.trim();
      if (color) return color;
    }
    return '#C89F66';
  }

  private resizeObserver?: ResizeObserver;

  // Custom Leaflet Icons to prevent default broken image paths in Angular
  private defaultIcon = L.icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
  });

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    setTimeout(() => {
      if (this.mapElement?.nativeElement) {
        this.initMap();
      }
    }, 0);
  }

  private initMap() {
    L.Marker.prototype.options.icon = this.defaultIcon;

    this.map = L.map(this.mapElement.nativeElement, {
      zoomControl: false,
    }).setView([45.4642, 9.19], 12);

    L.control.zoom({ position: 'bottomright' }).addTo(this.map);

    // TileLayer Humanitarian OpenStreetMap: fabbricati caldi, strade bianche e icone discrete, 100% gratuito senza API key
    L.tileLayer('https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Tiles style by <a href="https://www.hotosm.org/">Humanitarian OSM Team</a>',
      maxZoom: 19,
      subdomains: ['a', 'b', 'c'],
    }).addTo(this.map);

    if (typeof ResizeObserver !== 'undefined' && this.mapElement?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
      this.resizeObserver.observe(this.mapElement.nativeElement);
    }

    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
      }
    }, 200);

    let initialValue = this.model?.value;
    if (typeof initialValue === 'string') {
      try {
        initialValue = JSON.parse(initialValue);
      } catch (e) {
        // ignore parse error
      }
    }
    if (
      initialValue &&
      Array.isArray(initialValue) &&
      initialValue.length > 2
    ) {
      this.polygonPath = initialValue.map((p: any) =>
        Array.isArray(p) ? L.latLng(p[0], p[1]) : L.latLng(p.lat, p.lng)
      );
      this.isDrawingMode = false;
      this.isPolygonClosed = true;
      this.drawPolygon();
      this.renderVertexHandles();
      const bounds = L.latLngBounds(this.polygonPath);
      this.map.fitBounds(bounds);
    }

    this.map.on('click', (e: L.LeafletMouseEvent) => this.onMapClick(e));
    this.map.on('mousemove', (e: L.LeafletMouseEvent) => this.onMouseMove(e));
  }

  private createSquareIcon(isFirst: boolean = false): L.DivIcon {
    const size = isFirst ? 15 : 13;
    const border = isFirst ? '2.5px solid' : '2px solid';
    return L.divIcon({
      className: 'map-polygon-handle-icon',
      html: `<div class="map-polygon-handle-box ${isFirst ? 'is-first' : ''}" style="width: ${size}px; height: ${size}px; border: ${border} ${this.primaryColor};"></div>`,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  }

  private renderVertexHandles() {
    this.clearVertexMarkers();
    this.polygonPath.forEach((pt, idx) => {
      const isFirst = this.isDrawingMode && !this.isPolygonClosed && idx === 0;
      const marker = L.marker(pt, {
        icon: this.createSquareIcon(isFirst),
        zIndexOffset: isFirst ? 1000 : 500,
        interactive: isFirst,
      }).addTo(this.map);

      if (isFirst) {
        marker.bindTooltip('Clicca per chiudere l\'area', {
          direction: 'top',
          offset: [0, -8],
          className: 'map-polygon-tooltip',
        });
        marker.on('click', (e) => {
          L.DomEvent.stopPropagation(e);
          if (this.polygonPath.length > 2) {
            this.closePolygon();
          }
        });
      }

      this.vertexMarkers.push(marker);
    });
  }

  private clearVertexMarkers() {
    this.vertexMarkers.forEach((m) => m.remove());
    this.vertexMarkers = [];
  }

  private onMapClick(e: L.LeafletMouseEvent) {
    if (!this.isDrawingMode || this.isPolygonClosed || this.model.isReadOnly)
      return;

    // Chiude solo se si clicca/tocca DIRETTAMENTE sul primo vertice a schermo (tolleranza 22 pixel), NON in metri!
    if (this.polygonPath.length > 2) {
      const firstPoint = this.polygonPath[0];
      const p1 = this.map.latLngToContainerPoint(firstPoint);
      const p2 = this.map.latLngToContainerPoint(e.latlng);
      if (p1.distanceTo(p2) < 22) {
        this.closePolygon();
        return;
      }
    }

    this.polygonPath.push(e.latlng);
    if (this.tempPolygon) {
      this.tempPolygon.remove();
      this.tempPolygon = null;
    }
    this.drawCurrentPath();
    this.renderVertexHandles();
  }

  private onMouseMove(e: L.LeafletMouseEvent) {
    if (
      !this.isDrawingMode ||
      this.isPolygonClosed ||
      this.polygonPath.length === 0
    )
      return;
    this.updateTempPolygon(e.latlng);
  }

  private updateTempPolygon(currentLatLng: L.LatLng) {
    if (this.tempPolygon) {
      this.tempPolygon.remove();
    }
    const path = [...this.polygonPath, currentLatLng];
    this.tempPolygon = L.polygon(path, {
      color: this.primaryColor,
      fillColor: this.primaryColor,
      fillOpacity: 0.18,
      weight: 2.8,
    }).addTo(this.map);
  }

  private drawCurrentPath() {
    if (this.currentPolygon) {
      this.currentPolygon.remove();
    }
    if (this.polygonPath.length > 0) {
      this.currentPolygon = L.polygon(this.polygonPath, {
        color: this.primaryColor,
        fillColor: this.primaryColor,
        fillOpacity: this.isPolygonClosed ? 0.22 : 0.14,
        weight: 2.8,
      }).addTo(this.map);
    }
  }

  private drawPolygon() {
    if (this.tempPolygon) {
      this.tempPolygon.remove();
      this.tempPolygon = null;
    }
    this.drawCurrentPath();
  }

  public closePolygon() {
    if (this.polygonPath.length > 2) {
      this.isDrawingMode = false;
      this.isPolygonClosed = true;
      if (this.tempPolygon) {
        this.tempPolygon.remove();
        this.tempPolygon = null;
      }
      this.drawPolygon();
      this.renderVertexHandles();
      this.emitValue();
    }
  }

  public undoLastPoint() {
    if (!this.isDrawingMode || this.isPolygonClosed || this.polygonPath.length === 0)
      return;
    this.polygonPath.pop();
    if (this.tempPolygon) {
      this.tempPolygon.remove();
      this.tempPolygon = null;
    }
    this.drawCurrentPath();
    this.renderVertexHandles();
    this.emitValue();
  }

  clearPolygon() {
    if (this.model.isReadOnly) return;
    this.polygonPath = [];
    this.isDrawingMode = true;
    this.isPolygonClosed = false;
    this.clearVertexMarkers();
    if (this.tempPolygon) {
      this.tempPolygon.remove();
      this.tempPolygon = null;
    }
    if (this.currentPolygon) {
      this.currentPolygon.remove();
      this.currentPolygon = null;
    }
    this.emitValue();
  }

  private emitValue() {
    const value = this.polygonPath.map(p => ({ lat: p.lat, lng: p.lng }));
    this.model.value = value;
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.clearVertexMarkers();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.map) {
      this.map.remove();
    }
  }
}

registerMapPolygonQuestion();
